import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../utils/base_url";
import "../styles/SearchResults.css";

/* ── Skeleton row — replaces react-loading-skeleton dependency ── */
const SkeletonRow = () => (
  <div className="search-skeleton-item">
    <div className="skel-thumb" />
    <div className="skel-lines">
      <div className="skel-line w-60" />
      <div className="skel-line w-40" />
      <div className="skel-line w-30" />
    </div>
  </div>
);

const SearchResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ── sync URL query → state ── */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get("q") || "";
    setSearchTerm(query);
    if (query) fetchSearchResults(query);
  }, [location.search]);

  const fetchSearchResults = async (query) => {
    try {
      setLoading(true);
      const res = await api.get(`products/search/?search=${query}`);
      setResults(res.data.data || []);
    } catch (err) {
      console.error("Search failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.trim().length >= 1) fetchSearchResults(value);
    else setResults([]);
  };

  return (
    <div className="search-page-wrapper">

      {/* ── Sticky search header ── */}
      <div className="search-header">
        <button
          className="back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <i className="fa fa-arrow-left" aria-hidden="true"></i>
        </button>

        {/* Inline search input — lives in the sticky header */}
        <div className="search-input-wrapper" style={{ flex: 1, marginBottom: 0 }}>
          <i className="fa fa-search search-icon" aria-hidden="true"></i>
          <input
            type="search"
            className="form-control search-input"
            placeholder="Search cashews, natural products…"
            value={searchTerm}
            onChange={handleChange}
            autoFocus
            aria-label="Search products"
          />
          {searchTerm && (
            <button
              className="clear-btn"
              onClick={() => { setSearchTerm(""); setResults([]); }}
              aria-label="Clear search"
            >
              <i className="fa fa-times" aria-hidden="true"></i>
            </button>
          )}
        </div>
      </div>

      {/* ── Main content: centered, max-width column ── */}
      <div className="container" style={{ paddingBlock: "var(--space-4)" }}>
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">

            {/* Loading skeletons */}
            {loading && (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            )}

            {/* Empty state */}
            {!loading && results.length === 0 && searchTerm.length > 1 && (
              <div className="search-empty">
                <span className="search-empty-icon">🌱</span>
                <p className="search-empty-title">
                  Nothing found for "{searchTerm}"
                </p>
                <p className="search-empty-sub">
                  Try different keywords or browse our categories
                </p>
              </div>
            )}

            {/* Results */}
            {!loading && results.length > 0 && (
              <>
                <p className="search-results-label">
                  {results.length} result{results.length !== 1 ? "s" : ""}
                </p>

                <div className="search-results">
                  {results.map((product) => {
                    const hasOffer =
                      product.offer_percentage &&
                      Number(product.offer_percentage) > 0 &&
                      product.offer_price;

                    const outOfStock =
                      !product.is_available || product.stock_quantity === 0;

                    const lowStock =
                      product.stock_quantity <= 8 && product.stock_quantity > 0;

                    return (
                      <div
                        key={product.id}
                        className="product-row"
                        onClick={() => !outOfStock && navigate(`/product/${product.id}`)}
                        style={outOfStock ? { opacity: 0.55, pointerEvents: "none" } : {}}
                        role="button"
                        tabIndex={outOfStock ? -1 : 0}
                        onKeyDown={(e) => {
                          if (!outOfStock && (e.key === "Enter" || e.key === " "))
                            navigate(`/product/${product.id}`);
                        }}
                        aria-label={`View ${product.product_name}`}
                      >
                        {/* Image with inline badges */}
                        <div className="product-img-wrap">
                          <img
                            src={product.product_image}
                            alt={product.product_name}
                            className="product-thumb"
                            loading="lazy"
                          />
                          {/* Badges live INSIDE the image wrap — responsive by nature */}
                          <div className="badge-img-wrap">
                            {hasOffer && (
                              <span className="offer">
                                {Math.round(product.offer_percentage)}% OFF
                              </span>
                            )}
                            {outOfStock && (
                              <span className="out-of-stock">Out of stock</span>
                            )}
                            {!outOfStock && lowStock && (
                              <span className="stock-hurry">
                                Only {product.stock_quantity} left
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Info */}
                        <div className="product-info-block">
                          <p className="product-row-name">{product.product_name}</p>
                          {product.product_description && (
                            <p className="product-row-desc">
                              {product.product_description.slice(0, 60)}…
                            </p>
                          )}

                          <div className="product-row-price">
                            {hasOffer ? (
                              <>
                                <span className="price-offer">
                                  ₹{parseFloat(product.offer_price).toLocaleString()}
                                </span>
                                <span className="price-original">
                                  ₹{parseFloat(product.price).toLocaleString()}
                                </span>
                              </>
                            ) : (
                              <span className="price-regular">
                                ₹{parseFloat(product.price).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* ADD button — stop propagation so row click doesn't also fire */}
                        <div
                          className="product-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            className="btn-add-search"
                            onClick={() => navigate(`/product/${product.id}`)}
                            disabled={outOfStock}
                            aria-label={`Add ${product.product_name} to cart`}
                          >
                            {outOfStock ? "N/A" : "ADD"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResults;